<template>
    <div
        class="component-text-with-image-carousel"
        :class="carouselTextWithImageClass"
    >
        <div class="carousel">
            <div
                ref="container"
                class="slides"
            >
                <div
                    v-for="(slide, index) in data.slides"
                    :key="index"
                    ref="elements"
                    class="slide"
                >
                    <ComponentTextWithImage
                        :background="background"
                        :data="slide"
                        :has-eager-image="hasEagerImage"
                    />
                </div>
            </div>
        </div>

        <ButtonRound
            :disabled="isAtStart"
            class="left"
            icon="angle-left"
            :aria-label="t('catalogue.carousel.button_prev')"
            @click="previous"
        />

        <ButtonRound
            :disabled="isAtEnd"
            class="right"
            icon="angle-right"
            :aria-label="t('catalogue.carousel.button_next')"
            @click="next"
        />

        <div class="carousel-pagination">
            <div
                v-for="index in pageCount"
                :key="index"
                :class="getClassDot(index)"
                class="carousel-page"
                @click.prevent.stop="goToPage(index)"
            ></div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'
import { useI18n } from 'vue-i18n'

import type {
    Color,
    ComponentTextWithImageCarouselFragment,
} from '@/graphql/api'
import { useCarousel } from '@/components/layout/carousels/base/useCarouselNew'
import { useCarouselDotsPage } from '@/components/layout/carousels/base/useCarouselDotsPage'

import ComponentTextWithImage from '@/components/cms/component/ComponentTextWithImage.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'

const { data } = defineProps<{
    data: ComponentTextWithImageCarouselFragment
    background: Color
    hasEagerImage?: boolean
}>()

const { t } = useI18n()

const carouselTextWithImageClass = computed(() => {
    return {
        [`padding-${data.slidePadding.toLocaleLowerCase()}`]: true,
    }
})

const {
    container,
    elements,
    slideWidth,
    previous,
    next,
    current,
    isAtStart,
    isAtEnd,
} = useCarousel(toRef(() => data.slides))

const { pageCount, getClassDot, goToPage } = useCarouselDotsPage(
    container,
    slideWidth,
    data.slides,
    current
)
</script>

<style lang="scss" scoped>
.component-text-with-image-carousel {
    position: relative;
    margin: auto;
    z-index: 10;
    max-width: 1440px;

    @include hover() {
        z-index: 11;
    }

    &.padding-none {
        .carousel {
            .slides {
                scroll-padding: 0;
                padding: 0;
            }
        }
    }

    &.padding-small {
        .carousel {
            .slides {
                scroll-padding: 0 20px;
                padding: 0 20px;

                .slide {
                    margin: 0 0 0 20px;
                }
            }
        }
    }

    &.padding-medium {
        .carousel {
            .slides {
                scroll-padding: 0 20px;
                padding: 0 20px;

                @include min-width(768) {
                    scroll-padding: 0 40px;
                    padding: 0 40px;
                }

                .slide {
                    margin: 0 0 0 20px;

                    @include min-width(768) {
                        margin: 0 0 0 40px;
                    }
                }
            }
        }
    }

    &.padding-large {
        .carousel {
            .slides {
                scroll-padding: 0 20px;
                padding: 0 20px;

                @include min-width(768) {
                    scroll-padding: 0 60px;
                    padding: 0 60px;
                }

                .slide {
                    margin: 0 0 0 20px;

                    @include min-width(768) {
                        margin: 0 0 0 60px;
                    }
                }
            }
        }
    }

    .carousel {
        width: 100%;
        height: auto;

        .slides {
            position: relative;
            // width: 100%;
            display: flex;
            overflow-y: hidden;
            overflow-x: scroll;
            scroll-snap-type: x mandatory;
            scroll-padding: 0 40px;
            padding: 0 40px;
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */

            &::-webkit-scrollbar {
                display: none;
            }

            &::after {
                content: '';
                padding-right: 40px;

                @include min-width(1024) {
                    padding-right: 0;
                }
            }

            @include min-width(1360) {
                padding: 0;
                scroll-padding: 0;
            }

            .slide {
                position: relative;
                user-select: none;
                scroll-snap-align: start;
                width: 100%;
                flex-shrink: 0;

                :deep(.text-with-image.rounded-image) {
                    .image {
                        width: 220px;
                        height: 320px;
                        border-radius: 110px;

                        @include min-width(768) {
                            width: 335px;
                            height: 500px;
                            border-radius: 200px;
                        }
                    }
                }
            }
        }
    }

    .button-round {
        position: absolute;
        z-index: 3;
        top: calc(50% - 25px);
        display: none;

        @include min-width(1024) {
            display: block;
        }

        &.disabled {
            opacity: 0;
        }

        &.left {
            left: 15px;

            @include min-width(1600) {
                left: -20px;
            }
        }

        &.right {
            right: 15px;

            @include min-width(1600) {
                right: -20px;
            }
        }
    }

    .carousel-pagination {
        display: flex;
        justify-content: center;
        padding: 20px 0;

        @include min-width(1024) {
            display: none;
        }

        .carousel-page {
            flex: 0 0 7px;
            height: 7px;
            background-color: $grey-200;
            margin: 4px;
            border-radius: 50%;
            cursor: pointer;
            transition: background-color 80ms ease-in-out;

            &.active {
                background-color: $blue;
            }
        }
    }
}
</style>
